<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.customerStatistics') }}</b-card-title>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        size="sm"
        :disabled="isLoading"
        @click="refreshData"
      >
        <feather-icon
          icon="RefreshCwIcon"
          :class="{ 'animate-spin': isLoading }"
        />
      </b-button>
    </b-card-header>
    <b-card-body v-if="customers">
      <!-- Total Customers -->
      <div class="text-center mb-2">
        <h2 class="font-weight-bolder mb-1">
          {{ customers.total_customers }}
        </h2>
        <span class="text-muted">{{ $t('dashboard.totalCustomers') }}</span>
      </div>

      <!-- Status Distribution -->
      <b-row class="mb-2">
        <b-col cols="6">
          <b-card
            bg-variant="success"
            text-variant="white"
            class="text-center mb-0"
          >
            <h3 class="text-white mb-0">
              {{ customers.by_status.active }}
            </h3>
            <small>{{ $t('customer.status.active') }}</small>
          </b-card>
        </b-col>
        <b-col cols="6">
          <b-card
            bg-variant="danger"
            text-variant="white"
            class="text-center mb-0"
          >
            <h3 class="text-white mb-0">
              {{ customers.by_status.inactive }}
            </h3>
            <small>{{ $t('customer.status.inactive') }}</small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Type Distribution -->
      <h6 class="mb-1">
        {{ $t('dashboard.byType') }}
      </h6>
      <vue-apex-charts
        type="pie"
        height="200"
        :options="typeChartOptions"
        :series="typeChartSeries"
      />

      <!-- Recently Added -->
      <div class="text-center mt-2">
        <b-card
          bg-variant="info"
          text-variant="white"
        >
          <h4 class="text-white mb-0">
            {{ customers.recent_customers }}
          </h4>
          <span>{{ $t('dashboard.recentCustomers') }}</span>
          <small class="d-block">{{ $t('dashboard.lastThirtyDays') }}</small>
        </b-card>
      </div>

      <!-- Top Customers -->
      <h6 class="mb-1 mt-2">
        {{ $t('dashboard.topCustomers') }}
      </h6>
      <b-list-group>
        <b-list-group-item
          v-for="customer in customers.top_customers"
          :key="customer.id"
          class="d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>{{ customer.name }}</strong>
            <br>
            <small class="text-muted">{{ $t(`customer.type.${customer.type}`) }}</small>
          </div>
          <b-badge
            variant="primary"
            pill
          >
            {{ customer.projects_count }} {{ $t('dashboard.projects') }}
          </b-badge>
        </b-list-group-item>
      </b-list-group>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'CustomerStatisticsWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BListGroup,
    BListGroupItem,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  computed: {
    ...mapGetters('dashboard', ['customers', 'loading']),
    isLoading() {
      return this.loading.customers
    },
    typeChartSeries() {
      if (!this.customers || !this.customers.by_type) return []
      return Object.values(this.customers.by_type)
    },
    typeChartOptions() {
      if (!this.customers || !this.customers.by_type) return {}
      return {
        labels: Object.keys(this.customers.by_type).map(key => this.$t(`customer.type.${key}`)),
        colors: ['#3b82f6', '#10b981'],
        legend: {
          position: 'bottom',
        },
      }
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchCustomers', 'refreshWidget']),
    refreshData() {
      this.refreshWidget({ widget: 'customers' })
    },
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
